// components/SelectionSendButton.jsx
import React, { useState, useEffect, useRef } from 'react';
import './SelectionSendButton.css';

const SelectionSendButton = ({ 
  selectionRects, 
  selectedText, 
  selectionContext,
  hasHighlightOnSelection,
  matchingHighlightIds,
  onSend,
  onAddHighlight,
  onRemoveHighlight,
  onRemoveMultipleHighlights,
  zoom = 1 
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [preventRemoveButton, setPreventRemoveButton] = useState(false);
  const timeoutRef = useRef(null);
  const lastAddHighlightTimeRef = useRef(0);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Show button when there's a valid selection
    const hasValidSelection = selectionRects && selectionRects.length > 0 && selectedText;
    setIsVisible(hasValidSelection);
    
    // Reset preventRemoveButton when selection changes (new selection)
    if (hasValidSelection) {
      // Check if we just added a highlight (within last 1 second)
      const timeSinceLastAdd = Date.now() - lastAddHighlightTimeRef.current;
      if (timeSinceLastAdd > 1000) {
        setPreventRemoveButton(false);
      }
    }
  }, [selectionRects, selectedText, selectionContext, hasHighlightOnSelection, matchingHighlightIds]);

  if (!isVisible || !selectionRects || selectionRects.length === 0 || !selectedText) return null;

  // Use the first rectangle for positioning
  const firstRect = selectionRects[0];
  
  // Calculate position - below the selected text with a small gap
  const buttonStyle = {
    position: 'fixed',
    left: `${firstRect.left}px`,
    top: `${firstRect.top + firstRect.height + 10}px`, // 10px below the selection
    transform: `scale(${1/zoom})`,
    transformOrigin: 'top left',
    zIndex: 10000,
    display: 'flex',
    alignItems: 'center'
  };

  const handleSend = () => {
    // Ensure we have the full context data
    const contextToSend = selectionContext || { 
      selectedText: selectedText, 
      contextBefore: '', 
      contextAfter: '' 
    };
    
    onSend(contextToSend);
  };

  const handleAddHighlight = () => {
    if (onAddHighlight && selectedText) {
      // Record the time we added the highlight
      lastAddHighlightTimeRef.current = Date.now();
      
      // Prevent remove button from appearing for this selection
      setPreventRemoveButton(true);
      
      // Add the highlight
      onAddHighlight(selectedText);
      
      // Clear timeout if it exists
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      
      // Keep remove button prevented for 2 seconds (or until selection changes)
      timeoutRef.current = setTimeout(() => {
        setPreventRemoveButton(false);
      }, 2000);
      
      // Clear selection after adding highlight
      window.getSelection()?.removeAllRanges();
    }
  };

  const handleRemoveAllHighlights = () => {
    if (!matchingHighlightIds || matchingHighlightIds.length === 0) {
      return;
    }
    
    // Use the bulk remove function if available
    if (onRemoveMultipleHighlights) {
      onRemoveMultipleHighlights(matchingHighlightIds);
    } 
    // Otherwise, remove them one by one
    else if (onRemoveHighlight) {
      matchingHighlightIds.forEach(highlightId => {
        onRemoveHighlight(highlightId);
      });
    }
    
    // Clear selection after removing
    window.getSelection()?.removeAllRanges();
  };

  // Determine if we should show the remove button
  const shouldShowRemoveButton = 
    hasHighlightOnSelection && 
    matchingHighlightIds && 
    matchingHighlightIds.length > 0 && 
    !preventRemoveButton;

  return (
    <div className="selection-send-button" style={buttonStyle}>
      <div className="button-container">
        {/* 1. Send to Chat Button - Always shown when there's a selection */}
        <button 
          className="send-context-btn"
          onClick={handleSend}
          title="Send selected text to chat"
        >
          <span>📨</span>
          <span>Send</span>
        </button>
        
        {/* 2. Add Highlight Button - Always shown when there's a selection */}
        <button 
          className="add-highlight-btn"
          onClick={handleAddHighlight}
          title="Add highlight"
        >
          <span>🖍️</span>
          <span>Highlight</span>
        </button>
        
        {/* 3. Remove Highlight Button - Only shown when there's a highlight detected AND we didn't just add one */}
        {shouldShowRemoveButton && (
          <button 
            className="remove-highlight-btn"
            onClick={handleRemoveAllHighlights}
            title="Remove highlight"
          >
            <span>🗑️</span>
            <span>Remove</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default SelectionSendButton;